import { Link } from 'react-router-dom';
import { Badge, StatusDot, Pill } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { vehicleState, healthBand, healthReasons, alertTitle, SEVERITIES, ALERT_STATES } from '../../lib/domain';
import * as fmt from '../../lib/format';

/**
 * The small pieces of vehicle, device and alert presentation that appear on
 * more than one screen.
 *
 * A vehicle's registration renders identically in the dashboard list, the
 * vehicle grid, the alert inbox and the device detail page because it is this
 * one component — not four near-identical blocks that drift apart the first
 * time someone adds the fleet name to one of them.
 */

export function VehicleIdentity({ vehicle, link = true, showFleet = true }) {
  const body = (
    <>
      <span className="block truncate text-[13.5px] font-medium text-ink">{vehicle.reg_number}</span>
      {showFleet && (
        <span className="block truncate text-[11.5px] text-ink-3">
          {vehicle.fleet_name || 'Unassigned fleet'}
          {vehicle.model ? ` · ${vehicle.model}` : ''}
        </span>
      )}
    </>
  );

  if (!link) return <span className="block min-w-0">{body}</span>;

  return (
    <Link
      to={`/vehicles/${vehicle.id}`}
      onClick={(event) => event.stopPropagation()}
      className="block min-w-0 hover:underline underline-offset-2"
    >
      {body}
    </Link>
  );
}

export function VehicleStateBadge({ vehicle, size = 'md' }) {
  const state = vehicleState(vehicle);
  return (
    <Badge tone={state.tone} icon={state.icon} size={size}>
      {state.label}
    </Badge>
  );
}

/**
 * Health as a number plus its band. The band label is always present — the
 * colour is a second channel, never the only one.
 */
export function HealthBadge({ score, online, flags, showReasons = false }) {
  const band = healthBand({ score, online });
  const reasons = showReasons ? healthReasons(flags) : [];

  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge tone={band.tone} size="sm">
        {band.key === 'offline' || score === null || score === undefined ? band.label : `${score} · ${band.label}`}
      </Badge>
      {reasons.length > 0 && (
        <span
          className="text-[11.5px] text-ink-3"
          title={reasons.map((r) => `${r.label}: ${r.level}`).join('\n')}
        >
          {reasons.length} signal{reasons.length === 1 ? '' : 's'}
        </span>
      )}
    </span>
  );
}

export function LastSeen({ at, online }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <StatusDot tone={online ? 'good' : 'critical'} pulse={Boolean(online)} />
      <span className="text-[13px] text-ink-2">{fmt.relativeTime(at)}</span>
    </span>
  );
}

export function SeverityBadge({ severity, size = 'sm' }) {
  const meta = SEVERITIES[severity] || SEVERITIES.INFO;
  return (
    <Badge tone={meta.tone} size={size}>
      {meta.label}
    </Badge>
  );
}

export function AlertStateBadge({ state }) {
  const meta = ALERT_STATES[state] || ALERT_STATES.OPEN;
  return (
    <Badge tone={meta.tone} size="sm" icon={null}>
      {meta.label}
    </Badge>
  );
}

/** One alert, as it appears in the dashboard feed and the vehicle timeline. */
export function AlertItem({ alert, onAcknowledge, compact = false }) {
  const meta = SEVERITIES[alert.severity] || SEVERITIES.INFO;

  return (
    <li className="flex items-start gap-2.5 px-4 py-2.5">
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
          meta.tone === 'critical'
            ? 'bg-critical-soft text-critical-ink'
            : meta.tone === 'warning'
              ? 'bg-warning-soft text-warning-ink'
              : 'bg-brand-soft text-brand-ink'
        }`}
      >
        <Icon name={meta.tone === 'critical' ? 'critical' : 'warning'} size="xs" strokeWidth={2} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ink">
            {alertTitle(alert)}
          </p>
          <span className="shrink-0 text-[11.5px] text-ink-3">{fmt.relativeTime(alert.opened_at)}</span>
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-ink-3">
          {alert.vehicle_id ? (
            <Link to={`/vehicles/${alert.vehicle_id}`} className="hover:underline underline-offset-2">
              {alert.reg_number || 'Vehicle'}
            </Link>
          ) : (
            <span>Fleet-wide</span>
          )}
          {!compact && alert.message && (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{alert.message}</span>
            </>
          )}
        </p>
      </div>

      {onAcknowledge && alert.state === 'OPEN' && (
        <button
          type="button"
          onClick={() => onAcknowledge(alert)}
          className="shrink-0 rounded-md px-2 py-1 text-[12px] font-medium text-brand hover:bg-brand-soft"
        >
          Acknowledge
        </button>
      )}
    </li>
  );
}

/** Signal strength, shown as bars rather than a bare 0–31 number. */
export function SignalBars({ csq }) {
  const level = csq === null || csq === undefined ? 0 : csq >= 20 ? 4 : csq >= 15 ? 3 : csq >= 10 ? 2 : 1;
  const tone = level >= 3 ? 'bg-good' : level === 2 ? 'bg-warning' : 'bg-critical';

  return (
    <span className="inline-flex items-center gap-1.5" title={fmt.signal(csq)}>
      <span className="flex items-end gap-px" aria-hidden="true">
        {[3, 5, 7, 9].map((height, index) => (
          <span
            key={height}
            className={`w-0.5 rounded-sm ${index < level ? tone : 'bg-surface-3'}`}
            style={{ height }}
          />
        ))}
      </span>
      <span className="text-[13px] tabular-nums text-ink-2">{csq ?? '—'}</span>
    </span>
  );
}

export function CountPill({ value, label }) {
  return (
    <Pill>
      {value} {label}
    </Pill>
  );
}
